import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {ScrollRestoration | null} */
  #previousScrollRestoration = null;

  /** @type {boolean} — true for 2s after dialog close to block spurious auto-open */
  #autoOpenSuppressed = false;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #autoOpenSuppressTimer = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#clearAutoOpenSuppression);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener(DialogCloseEvent.eventName, this.#suppressAutoOpen);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#clearAutoOpenSuppression);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener(DialogCloseEvent.eventName, this.#suppressAutoOpen);
    if (this.#autoOpenSuppressTimer !== null) clearTimeout(this.#autoOpenSuppressTimer);
    this.#historyAbortController?.abort();
  }

  // Suppress auto-open for 2s after dialog closes to prevent cart-update AJAX
  // responses from re-opening the drawer after the user has dismissed it.
  #suppressAutoOpen = () => {
    this.#autoOpenSuppressed = true;
    if (this.#autoOpenSuppressTimer !== null) clearTimeout(this.#autoOpenSuppressTimer);
    this.#autoOpenSuppressTimer = setTimeout(() => {
      this.#autoOpenSuppressed = false;
      this.#autoOpenSuppressTimer = null;
    }, 2000);
  };

  // When the drawer opens again (user clicked the cart icon), lift the suppression
  // so the next close → add-to-cart cycle works normally.
  #clearAutoOpenSuppression = () => {
    if (this.#autoOpenSuppressTimer !== null) {
      clearTimeout(this.#autoOpenSuppressTimer);
      this.#autoOpenSuppressTimer = null;
    }
    this.#autoOpenSuppressed = false;
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      // Prevent the browser from restoring scroll position on `history.back()`,
      // which would otherwise fight the manual scroll restore in closeDialog().
      this.#previousScrollRestoration = history.scrollRestoration;
      history.scrollRestoration = 'manual';
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
    if (this.#previousScrollRestoration) {
      history.scrollRestoration = this.#previousScrollRestoration;
      this.#previousScrollRestoration = null;
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number }, data?: { source?: string } }>} event
   */
  #handleCartAdd = (event) => {
    // Only auto-open for genuine add-to-cart events, not cart modifications.
    // CartUpdateEvent from cart-items-component always sets source:'cart-items-component'.
    // The suppression flag blocks any event that arrives within 2s of the user closing the drawer.
    const isCartModification = event.detail?.data?.source === 'cart-items-component';

    // When the post-add-to-cart recommendations popup is enabled (present in the DOM), it
    // takes over deciding when the cart should open: show recs instead if there are any,
    // fall back to opening the cart immediately if there aren't, and open the cart whenever
    // the recs popup itself closes (see post-add-to-cart-popup.js). Don't race it here.
    const deferToRecsPopup = !isCartModification && !!document.querySelector('post-add-to-cart-popup');

    if (this.hasAttribute('auto-open') && !isCartModification && !this.#autoOpenSuppressed && !deferToRecsPopup) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
